import {inject} from "@angular/core";
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {Store} from "@ngrx/store";
import {MatSnackBar} from "@angular/material/snack-bar";
import {catchError, concatMap, distinctUntilChanged, EMPTY, exhaustMap, groupBy, map, mergeMap, of, switchMap, take, tap, timer} from "rxjs";
import {AlgorithmicDmvActions} from "./algorithmic-dmv.actions";
import {AlgorithmicDmvService} from "../services/algorithmic-dmv.service";
import {selectHasActiveJobsForFund} from "./algorithmic-dmv.selectors";
import {ErrorHandlingUtil} from "../../shared/utils/error-handling-util";
import {NotificationActions} from "../../notification/store/notification.actions";

export const startAlgorithmicDmv = createEffect(
    (actions$ = inject(Actions), service = inject(AlgorithmicDmvService), snack = inject(MatSnackBar)) =>
        actions$.pipe(
            ofType(AlgorithmicDmvActions.start),
            exhaustMap(({fundId, request}) =>
                service.start(fundId, request).pipe(
                    tap(() => snack.open("Algorithmic DMV running in background — we'll notify you when it's ready", "OK", {duration: 4000})),
                    map(taskInfoId => AlgorithmicDmvActions.startSuccess({fundId, taskInfoId})),
                    catchError(err => of(AlgorithmicDmvActions.startError({
                        fundId,
                        errorMsg: ErrorHandlingUtil.getErrorMessage(err)
                    })))
                )
            )
        ),
    {functional: true}
);

// After a successful start, immediately reload jobs for that fund so the chip flips to "running"
export const reloadJobsAfterStart = createEffect(
    (actions$ = inject(Actions)) =>
        actions$.pipe(
            ofType(AlgorithmicDmvActions.startSuccess),
            map(({fundId}) => AlgorithmicDmvActions.loadJobs({fundId}))
        ),
    {functional: true}
);

// Switch the notification bell's poll cadence to 5 s while the algorithmic DMV job is in flight.
// The matching JOB_FINISHED notification untracks itself when it arrives in the panel list.
export const trackAlgorithmicDmvJob = createEffect(
    (actions$ = inject(Actions)) =>
        actions$.pipe(
            ofType(AlgorithmicDmvActions.startSuccess),
            map(({taskInfoId}) => NotificationActions.trackJob({taskInfoId}))
        ),
    {functional: true}
);

export const loadJobs = createEffect(
    (actions$ = inject(Actions), service = inject(AlgorithmicDmvService)) =>
        actions$.pipe(
            ofType(AlgorithmicDmvActions.loadJobs),
            switchMap(({fundId}) =>
                service.getJobsForFund(fundId).pipe(
                    map(jobs => AlgorithmicDmvActions.loadJobsSuccess({fundId, jobs})),
                    catchError(err => of(AlgorithmicDmvActions.loadJobsError({
                        fundId,
                        errorMsg: ErrorHandlingUtil.getErrorMessage(err)
                    })))
                )
            )
        ),
    {functional: true}
);

// Poll every 5s while there's an active (NEW / IN_PROGRESS) job for the fund, and
// stop once none remain. groupBy(fundId) isolates funds so concurrent fund pages
// never cancel each other's polling. Within a fund, each loadJobsSuccess snapshots
// whether an active job exists; distinctUntilChanged means the timer is (re)started
// only on a false→true / true→false transition — NOT torn down by the loadJobsSuccess
// the poll itself produces, so the 5s cadence stays stable.
export const pollJobsWhileActive = createEffect(
    (actions$ = inject(Actions), store = inject(Store)) =>
        actions$.pipe(
            ofType(AlgorithmicDmvActions.loadJobsSuccess),
            groupBy(({fundId}) => fundId),
            mergeMap(fundGroup$ => {
                const fundId = fundGroup$.key;
                return fundGroup$.pipe(
                    switchMap(() => store.select(selectHasActiveJobsForFund(fundId)).pipe(take(1))),
                    distinctUntilChanged(),
                    switchMap(hasActive =>
                        hasActive
                            ? timer(5000, 5000).pipe(map(() => AlgorithmicDmvActions.loadJobs({fundId})))
                            : EMPTY
                    )
                );
            })
        ),
    {functional: true}
);

export const loadDetail = createEffect(
    (actions$ = inject(Actions), service = inject(AlgorithmicDmvService)) =>
        actions$.pipe(
            ofType(AlgorithmicDmvActions.loadDetail),
            switchMap(({taskInfoId}) =>
                service.getJobDetail(taskInfoId).pipe(
                    map(detail => AlgorithmicDmvActions.loadDetailSuccess({detail})),
                    catchError(err => of(AlgorithmicDmvActions.loadDetailError({
                        errorMsg: ErrorHandlingUtil.getErrorMessage(err)
                    })))
                )
            )
        ),
    {functional: true}
);

export const acceptResult = createEffect(
    (actions$ = inject(Actions), service = inject(AlgorithmicDmvService)) =>
        actions$.pipe(
            ofType(AlgorithmicDmvActions.acceptResult),
            // concatMap (not mergeMap) so accepts on the same asset run sequentially.
            // Each accept reads the asset valuation, mutates scenarios + company-level
            // fields, and saves — running them concurrently causes a last-write-wins
            // race on the same row.
            concatMap(({resultId, request, taskInfoId}) =>
                service.acceptResult(resultId, request).pipe(
                    map(() => AlgorithmicDmvActions.acceptResultSuccess({resultId, taskInfoId})),
                    catchError(err => of(AlgorithmicDmvActions.acceptResultError({
                        resultId,
                        errorMsg: ErrorHandlingUtil.getErrorMessage(err)
                    })))
                )
            )
        ),
    {functional: true}
);

export const skipResult = createEffect(
    (actions$ = inject(Actions), service = inject(AlgorithmicDmvService)) =>
        actions$.pipe(
            ofType(AlgorithmicDmvActions.skipResult),
            concatMap(({resultId, taskInfoId}) =>
                service.skipResult(resultId).pipe(
                    map(() => AlgorithmicDmvActions.skipResultSuccess({resultId, taskInfoId})),
                    catchError(err => of(AlgorithmicDmvActions.skipResultError({
                        resultId,
                        errorMsg: ErrorHandlingUtil.getErrorMessage(err)
                    })))
                )
            )
        ),
    {functional: true}
);

export const acceptAllSuggestions = createEffect(
    (actions$ = inject(Actions), service = inject(AlgorithmicDmvService)) =>
        actions$.pipe(
            ofType(AlgorithmicDmvActions.acceptAllSuggestions),
            // exhaustMap: ignore repeat clicks while a bulk run is in flight (it applies every pending row).
            exhaustMap(({taskInfoId}) =>
                service.acceptAllSuggestions(taskInfoId).pipe(
                    map(outcome => AlgorithmicDmvActions.acceptAllSuggestionsSuccess({taskInfoId, outcome})),
                    catchError(err => of(AlgorithmicDmvActions.acceptAllSuggestionsError({
                        taskInfoId,
                        errorMsg: ErrorHandlingUtil.getErrorMessage(err)
                    })))
                )
            )
        ),
    {functional: true}
);

// Honest per-run summary of the bulk apply (counts suggestion vs algorithm-fallback vs failed).
export const showBulkAcceptSummary = createEffect(
    (actions$ = inject(Actions), snack = inject(MatSnackBar)) =>
        actions$.pipe(
            ofType(AlgorithmicDmvActions.acceptAllSuggestionsSuccess),
            tap(({outcome}) => {
                const parts = [`${outcome.suggestionCount} suggestion(s)`, `${outcome.algorithmCount} algorithm`];
                if (outcome.failedCount > 0) parts.push(`${outcome.failedCount} failed (left pending)`);
                snack.open(`Applied ${outcome.suggestionCount + outcome.algorithmCount} of ${outcome.totalAttempted}: ${parts.join(", ")}.`,
                    "OK", {duration: 7000});
            })
        ),
    {functional: true, dispatch: false}
);

export const showBulkAcceptError = createEffect(
    (actions$ = inject(Actions), snack = inject(MatSnackBar)) =>
        actions$.pipe(
            ofType(AlgorithmicDmvActions.acceptAllSuggestionsError),
            tap(() => snack.open("Couldn't bulk-accept these DMVs. Please refresh and try again.", "OK", {duration: 6000}))
        ),
    {functional: true, dispatch: false}
);

// Surface a friendly message when an accept/skip fails (e.g. a 409 because the DMV changed since it
// was generated). Without this the button just silently re-enables and the action looks like a no-op.
// Deliberately a user-friendly message, not the raw technical error.
export const showResultActionError = createEffect(
    (actions$ = inject(Actions), snack = inject(MatSnackBar)) =>
        actions$.pipe(
            ofType(AlgorithmicDmvActions.acceptResultError, AlgorithmicDmvActions.skipResultError),
            tap(() => snack.open(
                "Couldn't update this DMV — it may have changed since it was generated. Please refresh and try again.",
                "OK",
                {duration: 6000}))
        ),
    {functional: true, dispatch: false}
);

// After accept/skip success, reload the detail so the result row updates
export const reloadDetailAfterAction = createEffect(
    (actions$ = inject(Actions)) =>
        actions$.pipe(
            ofType(AlgorithmicDmvActions.acceptResultSuccess, AlgorithmicDmvActions.skipResultSuccess,
                AlgorithmicDmvActions.acceptAllSuggestionsSuccess),
            map(({taskInfoId}) => AlgorithmicDmvActions.loadDetail({taskInfoId}))
        ),
    {functional: true}
);
